/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningRegis.module.scss';

import { RegisPrompt } from './RegisPrompt';

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path
      d="M2.6 8h10M8.4 3.8 12.6 8l-4.2 4.2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const REGIS_POINTS = [
  'Our own AI model, in the platform and over the API',
  'Plans which registries can answer, then calls them',
  'Returns every value with its registry, reference and timestamp',
  'Cannot answer from training, only from what it retrieved',
  'Output goes into a committee paper without a second pass by an analyst',
];

export const InvestmentScreeningRegis = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = rootRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="#DCE2F5" className={styles.root}>
      <div ref={rootRef} className={cn(styles.grid, styles.reveal)}>
        <div className={styles.copy}>
          <TitleBlock
            align="left"
            title={t('Then ask the same record a question')}
            description={t(
              'The filed accounts, the ownership chain and the transaction history all sit behind one question box.',
            )}
          />

          <ul className={styles.list}>
            {REGIS_POINTS.map((point) => (
              <li key={point}>{t(point)}</li>
            ))}
          </ul>

          <div className={styles.actions}>
            <Button type="transparent" size="xl" href="/regis" suffix={<ArrowIcon />}>
              {t('See Regis')}
            </Button>
            <Button type="transparent" size="xl" href="https://api.globaldatabase.com/docs/v2/#setup" suffix={<ArrowIcon />}>
              {t('Read the API docs')}
            </Button>
          </div>
        </div>

        <div className={styles.chatWrap}>
          <RegisPrompt />
        </div>
      </div>
    </Section>
  );
};
